import React, { useState, useContext } from 'react';
import { Box, Button } from '@mui/material';
import { useTranslation } from 'react-i18next';
import AppContext from "../../../../../src/config/AppContext";

const CopyAndExport = ({ textToCopy }) => {

    const { t } = useTranslation();
    const [copied, setCopied] = useState(false);
    const [showExportOptions, setShowExportOptions] = useState(false);
    const { mode } = useContext(AppContext);
    const handleCopy = async () => {
        try {
            const htmlContent = textToCopy;
            const tempDiv = document.createElement('div');
            tempDiv.innerHTML = htmlContent;

            const extractTextWithNewlines = (element) => {
                let result = '';
                for (const node of element.childNodes) {
                    if (node.nodeType === Node.TEXT_NODE) {
                        result += node.textContent;
                    } else if (node.nodeType === Node.ELEMENT_NODE) {
                        const tag = node.tagName.toLowerCase();

                        if (['br'].includes(tag)) {
                            result += '\n';
                        } else if (['p', 'div', 'li'].includes(tag)) {
                            result += extractTextWithNewlines(node) + '\n';
                        } else {
                            result += extractTextWithNewlines(node);
                        }
                    }
                }
                return result;
            };

            const plainText = extractTextWithNewlines(tempDiv).replace(/\n{3,}/g, '\n\n');

            // const text = htmlContent.replace(/<[^>]+>/g, '');
            await navigator.clipboard.writeText(plainText);
            setCopied(true);
            setShowExportOptions(false);
            setTimeout(() => setCopied(false), 1000);
        } catch (err) {
            console.error('Failed to copy:', err);
        }
    };

    const getTimestampForFilename = () => {
        const timestamp = new Date().toISOString().replace(/[:.-]/g, '');
        return timestamp;
    };

    const handleExportClick = () => {
        setShowExportOptions(!showExportOptions);
    };

    const handleExport = (type) => {
        const htmlContent = textToCopy;
        let blob;
        let fileName = `${getTimestampForFilename()}-timesheetSummary.${type}`;

        if (type === 'txt') {
            const tempDiv = document.createElement('div');
            tempDiv.innerHTML = htmlContent;

            const extractTextWithNewlines = (element) => {
                let result = '';
                for (const node of element.childNodes) {
                    if (node.nodeType === Node.TEXT_NODE) {
                        result += node.textContent;
                    } else if (node.nodeType === Node.ELEMENT_NODE) {
                        const tag = node.tagName.toLowerCase();

                        if (['br'].includes(tag)) {
                            result += '\n';
                        } else if (['p', 'div', 'li'].includes(tag)) {
                            result += extractTextWithNewlines(node) + '\n';
                        } else {
                            result += extractTextWithNewlines(node);
                        }
                    }
                }
                return result;
            };

            const plainText = extractTextWithNewlines(tempDiv).replace(/\n{3,}/g, '\n\n');

            blob = new Blob([plainText], { type: 'text/plain;charset=utf-8' });
        } else if (type === 'word') {
            const header = `
              <html xmlns:o="urn:schemas-microsoft-com:office:office" 
                    xmlns:w="urn:schemas-microsoft-com:office:word" 
                    xmlns="http://www.w3.org/TR/REC-html40">
              <head><meta charset="utf-8"></head><body>`;
            const footer = "</body></html>";
            const sourceHTML = header + htmlContent + footer;
            blob = new Blob(['\ufeff', sourceHTML], { type: 'application/msword' });
            fileName = `${getTimestampForFilename()}-timesheetSummary.doc`;
        }
        else if (type === 'pdf') {
            import('html2pdf.js').then((html2pdf) => {
                const element = document.createElement('div');

                const styledTextToCopy = `
      <style>
        h2 {
          font-size: 20px;
          font-weight: bold;
          margin-top: 20px;
          margin-bottom: 10px;
        }
        p {
          font-size: 14px;
          margin-bottom: 10px;
        }
        li {
          font-size: 14px;
        }
        body {
          font-family: Arial, sans-serif;
        }
      </style>
      <div>
        ${textToCopy}
      </div>
    `;

                element.innerHTML = styledTextToCopy;

                element.style.backgroundColor = '#ffffff';
                element.style.color = '#242424';

                const opt = {
                    margin: [10, 10, 10, 10],
                    filename: `${getTimestampForFilename()}-timesheetSummary.pdf`,
                    html2canvas: { scale: 2 },
                    jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' }
                };

                html2pdf.default().set(opt).from(element).save();
            });

            return;
        }

        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = fileName;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
        setShowExportOptions(false);
    };

    function processTextSummary(text) {
        const summary = text?.replace(/\"/g, "").trim(); // Clean up the summary text
        const startIndex = summary.indexOf("Projects:");
        const cleanSummary = summary.substring(startIndex).trim();
        const lines = cleanSummary.split("\n"); // Split the summary into lines

        let htmlSummary = '<div class="summary">';
        lines.forEach((line) => {
            if (line.includes(":")) {
                const [label, content] = line.split(":", 2);
                htmlSummary += `<p><strong>${label.trim()}:</strong> ${content.trim()}</p>`;
            } else {
                htmlSummary += `<p>${line.trim()}</p>`;
            }
        });
        htmlSummary += "</div>";
        return htmlSummary;
    }

    return (
        <Box display="flex" flexDirection="column" gap={2}>
            <Box display="flex" flexDirection="row" gap={2}>
                <button
                    variant="outlined"
                    onClick={handleCopy}
                    className={`${mode === "dark" ? "option-label-aiDark" : "option-label-ai"} ${copied ? "copied" : ""}`}
                    sx={{
                        minWidth: 120,
                        borderRadius: 5,
                    }}
                >
                    {copied ? t('Copied to clipboard') : t('Copy')}
                </button>

                <button
                    variant="outlined"
                    onClick={handleExportClick}
                    className={`${mode === "dark" ? "option-label-aiDark" : "option-label-ai"} ${showExportOptions ? "export-active" : ""}`}
                    sx={{
                        minWidth: 100,
                        borderRadius: 5,
                    }}
                >
                    {t("Export")}
                </button>
            </Box>

            {showExportOptions && (
                <Box display="flex" flexDirection="row" gap={2} mt={1}>
                    {['txt', 'word', 'pdf'].map((type) => (
                        <button
                            key={type}
                            variant="outlined"
                            onClick={() => handleExport(type)}
                            className={`${mode === "dark" ? "option-label-aiDark" : "option-label-ai"} export-type`}
                            sx={{
                                textTransform: 'lowercase',
                                borderRadius: '50px',
                                minWidth: 60
                            }}
                        >
                            {type}
                        </button>
                    ))}
                </Box>
            )}
        </Box>
    );
};

export default CopyAndExport;